import {
  SimpleProductFragment,
  AttributeSelectedColorfulOption
} from '@/fo/api/magento';

import { Characteristics, Expert } from '../types';

type MapAttribute = {
  [key: string]: {
    selected_options: AttributeSelectedColorfulOption[];
    value: string;
  };
};

export const attributesMapper = (
  attributesRaw: SimpleProductFragment['attributes']
) => {
  const items = attributesRaw?.items ?? [];

  const map: MapAttribute = Object.fromEntries(
    items
      .map((item) => [item && 'code' in item ? item.code : '', item])
      .filter(([code]) => code)
  );

  const getSelectedLabels = (code: string) =>
    map[code]?.selected_options
      ?.map((option) => option?.label.trim())
      .join(', ') || '';
  const getValue = (code: string) => map[code]?.value || '';
  const getOption = (code: string) => map[code]?.selected_options?.[0];

  const volume = getOption('volume') || null;
  const video = getValue('gallery_video') || null;
  const labels = map['label']?.selected_options;

  const expert: Expert = {
    name: getValue('export_opinion_name') || null,
    job: getValue('export_opinion_post') || null,
    text: getValue('export_opinion_text') || null,
    quote: getValue('export_opinion_quote') || null
  };

  const characteristics: Characteristics = items
    .filter(
      (item) =>
        item &&
        !item?.label?.includes('//') &&
        item?.code !== 'mindbox_id' &&
        ('selected_options' in item && item.selected_options
          ? getSelectedLabels(item.code) !== ''
          : getValue(item.code) !== '')
    )
    .map((item) => {
      return {
        label: item?.label,
        value:
          item && 'selected_options' in item && item.selected_options
            ? getSelectedLabels(item.code)
            : getValue(item?.code ?? '')
      };
    });

  const gammas = (() => {
    const prefixed = Object.keys(map)
      .filter((key) => key.startsWith('gamma_'))
      .map(getOption)
      .filter((option) => option?.value);
    if (prefixed.length > 0) return prefixed;
    const gamma = getOption('gamma');
    return gamma?.value ? [gamma] : [];
  })();

  const diagnosticLabels =
    map['diagnostic_label']?.selected_options
      ?.map((option) => option?.label.trim())
      .filter(Boolean) || [];

  return {
    brand: getOption('brand')?.value ? getOption('brand') : null,
    gammas,

    productGroup: getOption('product_group')?.value
      ? getOption('product_group')
      : null,

    name: getValue('name'),
    nameEng: getValue('name_eng'),
    subtitle: getValue('product_name_subtitle'),
    shortDescription: getValue('short_description'),
    description: getValue('description'),
    usageInstructions: getValue('usage_instructions'),
    composition: getValue('composition'),
    activeComponents: getValue('active_components'),
    volume,
    effects: getSelectedLabels('effect'),
    characteristics,
    purpose: getValue('purpose'),
    labels,
    video,
    expert,
    structure_image: getValue('structure_image'),
    visibility: getSelectedLabels('visibility'),
    mindboxId: getValue('mindbox_id'),
    brandCode: getOption('brand')?.value,
    diagnosticLabels
  };
};
